import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, Shield, Tv, Clock, Headphones, IndianRupee, 
  CloudRain, Wifi, Signal, Award, Users, ThumbsUp,
  CheckCircle, Star, Play, Phone
} from 'lucide-react';

const WhyChooseUs = () => {
  const advantages = [
    {
      icon: CloudRain,
      title: "Monsoon-Proof Signal",
      description: "Unlike DTH that loses signal during rain, our fiber-optic IPTV delivers crystal clear picture even in the heaviest monsoons. No more missed matches or shows during storms!"
    },
    {
      icon: Tv,
      title: "True 4K Picture Quality",
      description: "Experience television as it was meant to be seen. Our fiber connection delivers uncompressed 4K content with stunning clarity, vibrant colors, and smooth motion."
    },
    {
      icon: Zap,
      title: "Zero Buffering",
      description: "Dedicated fiber bandwidth means your TV stream never competes with other internet usage. Watch in perfect quality while your family streams, games, and browses."
    },
    {
      icon: Signal,
      title: "800+ Live Channels",
      description: "Access the largest collection of SD, HD, and 4K channels including regional content in 15+ languages. Sports, movies, news, kids - we have it all."
    },
    {
      icon: IndianRupee,
      title: "Better Value Than DTH",
      description: "Get more channels at lower monthly costs. No expensive hardware to buy, no annual maintenance charges, and no hidden fees. Simple, transparent pricing."
    },
    {
      icon: Shield,
      title: "No Dish Required",
      description: "Say goodbye to ugly dishes on your balcony or roof. No alignment issues, no LNB failures, no cable damage from storms. Just a clean, modern setup."
    }
  ];

  const comparisons = [
    { feature: "Works During Rain", iptv: true, dth: false },
    { feature: "True 4K Quality", iptv: true, dth: false },
    { feature: "No Dish Required", iptv: true, dth: false },
    { feature: "800+ Channels", iptv: true, dth: false },
    { feature: "No Hardware Purchase", iptv: true, dth: false },
    { feature: "Regional Content (15+ Languages)", iptv: true, dth: true },
    { feature: "Electronic Program Guide", iptv: true, dth: true },
    { feature: "Works Without Internet", iptv: false, dth: true }
  ];

  const stats = [
    { number: "10,000+", label: "Happy Customers" },
    { number: "800+", label: "Live Channels" },
    { number: "99.9%", label: "Uptime Guarantee" },
    { number: "24/7", label: "Customer Support" }
  ];

  const testimonials = [
    {
      name: "Rajesh Kumar",
      location: "Whitefield, Bangalore",
      text: "Switched from Tata Sky 2 years ago. The picture quality during IPL matches is incredible, and no signal loss during monsoons!",
      rating: 5
    },
    {
      name: "Priya Sharma",
      location: "Electronic City",
      text: "As a working professional, I love that I dont need to worry about dish alignment or technician visits. Everything just works.",
      rating: 5
    },
    {
      name: "Mohammed Farooq",
      location: "Jayanagar",
      text: "The Kannada channel collection is amazing. My parents are very happy with the regional content and the clear picture quality.",
      rating: 5
    }
  ];

  return (
    <div data-testid="why-choose-us-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A0A0A] to-gray-900 text-white py-20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 bg-[#D92323]/20 text-[#D92323] px-4 py-2 rounded-full mb-6">
              <Award size={18} />
              <span className="font-semibold text-sm">Bangalore's Trusted IPTV Provider</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
              Why Choose <span className="text-[#D92323]">A Digital?</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed mb-8">
              Experience the future of television with fiber-powered IPTV. Crystal clear picture quality, 
              rain or shine, with 800+ channels at your fingertips.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/demo" className="btn-primary inline-flex items-center gap-2">
                <Play size={18} /> Try Free Demo
              </Link>
              <Link to="/contact" className="btn-secondary inline-flex items-center gap-2">
                <Phone size={18} /> Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-extrabold text-[#D92323] mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="badge badge-featured mb-4">Our Advantages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              6 Reasons to Switch to A Digital
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover why thousands of Bangalore families have made the switch from traditional DTH to our fiber-powered IPTV service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#D92323]/10 rounded-2xl flex items-center justify-center mb-6">
                  <advantage.icon size={28} className="text-[#D92323]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{advantage.title}</h3>
                <p className="text-gray-600 leading-relaxed">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="section bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge badge-default mb-4">Comparison</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              A Digital IPTV vs Traditional DTH
            </h2>
            <p className="text-gray-600">See how fiber-powered IPTV outperforms satellite DTH</p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-3 bg-gray-900 text-white font-bold">
                <div className="p-4 text-left">Feature</div>
                <div className="p-4 text-center bg-[#D92323]">A Digital IPTV</div>
                <div className="p-4 text-center">Traditional DTH</div>
              </div>
              {comparisons.map((item, index) => (
                <div key={index} className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                  <div className="p-4 text-gray-700 font-medium">{item.feature}</div>
                  <div className="p-4 text-center">
                    {item.iptv ? (
                      <CheckCircle size={24} className="text-green-500 mx-auto" />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </div>
                  <div className="p-4 text-center">
                    {item.dth ? (
                      <CheckCircle size={24} className="text-green-500 mx-auto" />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge badge-featured mb-4">Customer Reviews</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-r from-red-600 to-red-700">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Experience Better TV?
          </h2>
          <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of happy customers who have already made the switch. Try our free demo today!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/demo" className="bg-white text-red-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-2">
              <Play size={18} /> Get Free Demo
            </Link>
            <a href="tel:9019206699" className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              <Phone size={18} /> Call: 9019206699
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
